/* BMG aligned rows. A presentation-only layer; product order and commerce are untouched. */
(() => {
  'use strict';
  if (window.BMGAlignedCollections) { window.BMGAlignedCollections.init(); return; }
  const instances = new Map();
  const expandIcon = '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" focusable="false"><path d="M9 4H4v5m11-5h5v5M4 15v5h5m11-5v5h-5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Find consecutive, similarly sized rows without cropping or reordering the images.
  function rowsFor(ratios, width, gap, mobile) {
    const target = mobile ? width * .43 : Math.min(340, Math.max(220, width / 4.2));
    const maxItems = mobile ? 2 : 4;
    const minWidth = mobile ? Math.min(112, width * .35) : Math.min(170, width * .17);
    const n = ratios.length, costs = Array(n + 1).fill(Infinity), choices = Array(n);
    costs[n] = 0;
    for (let i = n - 1; i >= 0; i--) {
      let sum = 0;
      for (let count = 1; count <= maxItems && i + count <= n; count++) {
        const group = ratios.slice(i, i + count);
        sum += ratios[i + count - 1];
        if (mobile && count > 1 && group.some(r => r >= 2.3)) break;
        const fullHeight = (width - gap * (count - 1)) / sum;
        if (count > 1 && group.some(r => fullHeight * r < minWidth)) continue;
        const last = i + count === n;
        const cap = mobile ? 620 : target * 1.8;
        const height = (last || count === 1) && fullHeight > cap ? Math.min(cap, mobile ? fullHeight : target * 1.1) : fullHeight;
        let penalty = 100 * Math.log(Math.max(.1, height / target)) ** 2 + 5;
        if (mobile && count === 1 && ratios[i] >= 2.3) penalty = 4;
        if (!mobile && height > cap) penalty += 180 * (height / cap - 1);
        if (height < fullHeight) penalty += 55 * (1 - height / fullHeight);
        const score = penalty + costs[i + count];
        if (score < costs[i]) { costs[i] = score; choices[i] = {count, height}; }
      }
    }
    const rows = [];
    for (let i = 0; i < n;) {
      const choice = choices[i] || {count:1, height:width / ratios[i]};
      rows.push({start:i, count:choice.count, height:choice.height});
      i += choice.count;
    }
    return rows;
  }
  function mount(root) {
    if (instances.has(root)) return;
    const grid = root.querySelector('.bmg-print-shop__grid');
    if (!grid) return;
    const abort = new AbortController();
    const on = (target, name, fn, options = {}) => target?.addEventListener(name, fn, {...options, signal:abort.signal});
    const icons = new Map(), imageSizes = new Map();
    let frame = 0, lastWidth = -1, toolbar = null, controls = [];
    let signature = '', lastCards = [];
    const ratioOf = card => {
      const image = card.querySelector('.bmg-print-shop__image');
      const ratio = Number(image?.getAttribute('width')) / Number(image?.getAttribute('height'));
      return Number.isFinite(ratio) && ratio > 0 ? ratio : 4 / 3;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(render); };
    function makeToolbar() {
      if (toolbar) return;
      const edition = root.querySelector('[data-bmg-edition-filter]');
      const sort = root.querySelector('[data-bmg-sort]');
      const host = root.querySelector('.bmg-print-shop__controls');
      if (!edition || !sort || !host) return;
      toolbar = document.createElement('div');
      toolbar.className = 'bmg-aligned-tools';
      toolbar.setAttribute('aria-label', 'Refine the collection');
      [[edition, 'Edition'], [sort, 'Sort by']].forEach(([source, text]) => {
        const label = document.createElement('label');
        const name = document.createElement('span'); name.textContent = text;
        const select = document.createElement('select');
        select.setAttribute('aria-label', text === 'Edition' ? 'Filter artworks by edition' : 'Sort artworks');
        [...source.options].forEach(option => select.add(new Option(option.text, option.value)));
        label.append(name, select); toolbar.append(label);
        controls.push({source, select});
        on(select, 'change', () => {
          source.value = select.value;
          source.dispatchEvent(new Event('change', {bubbles:true}));
          schedule();
        });
      });
      host.append(toolbar);
    }
    function render() {
      frame = 0;
      if (!root.classList.contains('bmg-collection-refined')) return;
      const width = Math.floor(grid.getBoundingClientRect().width * 100) / 100;
      if (width <= 0) return;
      const cards = [...grid.querySelectorAll('.bmg-print-shop__card')].filter(card => !card.hidden && card.style.display !== 'none');
      makeToolbar();
      const ready = cards.length === 0 || cards.some(card => card.hasAttribute('data-order'));
      controls.forEach(({source, select}) => { select.value = source.value; select.disabled = source.disabled || !ready; });
      if (!root.classList.contains('bmg-aligned-collection')) root.classList.add('bmg-aligned-collection');
      if (toolbar && !root.classList.contains('bmg-aligned-controls')) root.classList.add('bmg-aligned-controls');
      cards.forEach(card => {
        const trigger = card.querySelector('[data-bmg-view-larger]');
        if (trigger && !icons.has(trigger)) {
          icons.set(trigger, {html:trigger.innerHTML, title:trigger.getAttribute('title')});
          trigger.innerHTML = expandIcon;
          trigger.title = 'View larger';
        }
      });
      const mobile = width < 680, gap = mobile ? 12 : width < 1000 ? 20 : 24;
      const ratios = cards.map(ratioOf);
      const nextSignature = width + '|' + cards.map((card, i) => card.dataset.handle + ':' + ratios[i]).join('|');
      if (signature === nextSignature && cards.every((card, i) => card === lastCards[i])) return;
      signature = nextSignature; lastCards = cards;
      grid.style.setProperty('--bmg-aligned-gap', gap + 'px');
      const rows = rowsFor(ratios, width, gap, mobile);
      rows.forEach((row, rowIndex) => {
        const widths = ratios.slice(row.start, row.start + row.count).map(ratio => Math.floor((ratio * row.height - .06) * 100) / 100);
        const remaining = Math.max(0, width - widths.reduce((total, item) => total + item, 0) - gap * (row.count - 1) - .15);
        for (let i = row.start; i < row.start + row.count; i++) {
          const card = cards[i];
          // Leave a fraction of a pixel for flexbox rounding at all device pixel ratios.
          const itemWidth = widths[i - row.start];
          card.style.setProperty('--bmg-aligned-width', itemWidth + 'px');
          card.style.setProperty('--bmg-aligned-height', row.height.toFixed(3) + 'px');
          card.style.setProperty('--bmg-aligned-end-space', (i === row.start + row.count - 1 ? remaining : 0).toFixed(3) + 'px');
          card.dataset.bmgAlignedRow = String(rowIndex);
          const image = card.querySelector('.bmg-print-shop__image');
          if (image) {
            if (!imageSizes.has(image)) imageSizes.set(image, image.getAttribute('sizes'));
            image.setAttribute('sizes', Math.ceil(itemWidth) + 'px');
          }
        }
      });
    }
    const mutation = new MutationObserver(schedule);
    mutation.observe(grid, {childList:true, subtree:true, attributes:true, attributeFilter:['hidden']});
    const rootMutation = new MutationObserver(schedule);
    rootMutation.observe(root, {childList:true, attributes:true, attributeFilter:['class']});
    let resize;
    if ('ResizeObserver' in window) {
      resize = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width || 0;
        if (Math.abs(width - lastWidth) > .5) { lastWidth = width; schedule(); }
      });
      resize.observe(grid);
    } else on(window, 'resize', schedule, {passive:true});
    on(grid, 'load', schedule, {capture:true});
    on(root, 'change', schedule);
    on(window, 'popstate', schedule);
    function dispose() {
      abort.abort(); mutation.disconnect(); rootMutation.disconnect(); resize?.disconnect(); cancelAnimationFrame(frame);
      toolbar?.remove(); root.classList.remove('bmg-aligned-collection','bmg-aligned-controls');
      grid.style.removeProperty('--bmg-aligned-gap');
      grid.querySelectorAll('[data-bmg-aligned-row]').forEach(card => {
        card.removeAttribute('data-bmg-aligned-row');
        card.style.removeProperty('--bmg-aligned-width'); card.style.removeProperty('--bmg-aligned-height');
        card.style.removeProperty('--bmg-aligned-end-space');
      });
      icons.forEach((value, trigger) => {
        if (!trigger.hasAttribute('data-bmg-view-larger')) return;
        trigger.innerHTML = value.html;
        if (value.title === null) trigger.removeAttribute('title'); else trigger.title = value.title;
      });
      imageSizes.forEach((value, image) => value === null ? image.removeAttribute('sizes') : image.setAttribute('sizes', value));
      instances.delete(root);
    }
    instances.set(root, dispose); render();
  }
  function init() {
    if (!document.querySelector('[data-bmg-art-viewer-template]')) return;
    document.querySelectorAll('[data-bmg-catalogue]').forEach(mount);
  }
  document.addEventListener('shopify:section:load', () => { [...instances.values()].forEach(dispose => dispose()); init(); });
  document.addEventListener('shopify:section:unload', event => {
    [...instances.entries()].forEach(([root, dispose]) => { if (event.target.contains(root)) dispose(); });
  });
  window.addEventListener('pageshow', init);
  window.BMGAlignedCollections = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
